'use client'

import Link from '@/lib/react-compat/link'
import { CheckCircle, BarChart3, Shield, TrendingUp, Calendar, ChevronRight } from 'lucide-react'
import { useScrollAnimation } from '@/hooks/use-scroll-animation'
import { useI18n } from '@/components/i18n-provider'

const APPLE_EASE = 'ease-[cubic-bezier(0.28,0.11,0.32,1)]'

export function FeaturesSection() {
  const { t } = useI18n()
  const { elementRef: titleRef, isVisible: titleVisible } = useScrollAnimation()
  const { elementRef: listRef, isVisible: listVisible } = useScrollAnimation({ threshold: 0.05 })

  const features = [
    {
      icon: CheckCircle,
      title: t('features.items.recommendation.title'),
      description: t('features.items.recommendation.desc'),
      href: '/recommendation',
      span: 'lg:col-span-3',
    },
    {
      icon: BarChart3,
      title: t('features.items.yield.title'),
      description: t('features.items.yield.desc'),
      href: '/yield-prediction',
      span: 'lg:col-span-3',
    },
    {
      icon: Shield,
      title: t('features.items.disease.title'),
      description: t('features.items.disease.desc'),
      href: '/disease-detection',
      span: 'lg:col-span-2',
    },
    {
      icon: TrendingUp,
      title: t('features.items.price.title'),
      description: t('features.items.price.desc'),
      href: '/price-prediction',
      span: 'lg:col-span-2',
    },
    {
      icon: Calendar,
      title: t('features.items.calendar.title'),
      description: t('features.items.calendar.desc'),
      href: '/calendar',
      span: 'lg:col-span-2',
    },
  ]

  return (
    <section id="features" className="bg-[#f5f5f7] py-24 sm:py-28 dark:bg-[#161617]">
      <div className="container">
        <div
          ref={titleRef}
          className={`mx-auto mb-16 max-w-2xl text-center transition-all duration-700 ${APPLE_EASE} ${
            titleVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
          }`}
        >
          <h2 className="text-balance text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-5xl">
            {t('features.title')}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            {t('features.subtitle')}
          </p>
        </div>

        <div ref={listRef} className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <Link
                key={index}
                href={feature.href}
                className={`group flex flex-col rounded-[28px] bg-background p-8 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-16px_rgba(0,0,0,0.12)] dark:bg-white/[0.04] dark:shadow-none dark:hover:shadow-[0_20px_40px_-16px_rgba(0,0,0,0.5)] ${APPLE_EASE} ${feature.span} ${
                  listVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
                }`}
                style={{ transitionDelay: listVisible ? `${index * 80}ms` : '0ms' }}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-green-700/10 text-green-700 dark:bg-green-400/10 dark:text-green-400">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 text-xl font-semibold tracking-tight text-foreground">
                  {feature.title}
                </h3>
                <p className="mt-2 flex-1 leading-relaxed text-muted-foreground">
                  {feature.description}
                </p>
                <span className="mt-6 inline-flex items-center gap-0.5 text-sm font-medium text-green-700 dark:text-green-400">
                  {t('features.detail').replace(/\s*→\s*$/, '')}
                  <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </span>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}
